import React, { useMemo, useState } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import { Link as RouterLink, useHistory } from "react-router-dom";

import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import GlobalMessage from "components/GlobalMessage";
import { APP_STRINGS } from "constants/appStrings";

import heroImage from "assets/img/new-layout/ana-lucena.png";
import "./index.css";

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const hasWords = (value) => value.trim().split(/\s+/).filter(Boolean).length >= 2;
const onlyDigits = (value) => value.replace(/\D+/g, "");

export default function RegisterPage() {
  const history = useHistory();
  const initialForm = useMemo(
    () => ({
      profileType: "pf",
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      cpf: "",
      birthDate: "",
      companyName: "",
      tradeName: "",
      cnpj: "",
      contactName: "",
      acceptTerms: false,
    }),
    []
  );

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", severity: "" });

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleChange = (event) => {
    const { id, name, value, checked, type } = event.target;
    const key = id || name;
    setField(key, type === "checkbox" ? checked : value);
  };

  const handleProfileType = (profileType) => {
    setForm((prev) => ({ ...prev, profileType }));
    setErrors((prev) => ({ ...prev, profileType: undefined }));
  };

  const validate = () => {
    const nextErrors = {};

    if (form.profileType === "pf" && !hasWords(form.fullName)) {
      nextErrors.fullName = "Informe nome e sobrenome.";
    }

    if (!isEmail(form.email)) {
      nextErrors.email = "Informe um e-mail valido.";
    }

    if (!form.password || form.password.trim().length < 6) {
      nextErrors.password = "A senha deve ter pelo menos 6 caracteres.";
    }

    if (form.confirmPassword !== form.password) {
      nextErrors.confirmPassword = "As senhas precisam ser iguais.";
    }

    if (onlyDigits(form.phone).length < 10) {
      nextErrors.phone = "Informe um telefone valido com DDD.";
    }

    if (form.profileType === "pf") {
      if (onlyDigits(form.cpf).length !== 11) {
        nextErrors.cpf = "Informe um CPF com 11 digitos.";
      }

      if (!form.birthDate) {
        nextErrors.birthDate = "Informe a data de nascimento.";
      }
    }

    if (form.profileType === "pj") {
      if (!form.companyName.trim()) {
        nextErrors.companyName = "Informe a razao social.";
      }

      if (!hasWords(form.contactName)) {
        nextErrors.contactName = "Informe o nome do responsavel.";
      }

      if (onlyDigits(form.cnpj).length !== 14) {
        nextErrors.cnpj = "Informe um CNPJ com 14 digitos.";
      }
    }

    if (!form.acceptTerms) {
      nextErrors.acceptTerms = "E necessario aceitar os termos para continuar.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setFeedback({
        message: "Revise os campos para concluir o cadastro.",
        severity: "error",
      });
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    window.setTimeout(() => {
      setLoading(false);
      setFeedback({
        message:
          "Cadastro demonstrativo concluido. Redirecionando para o portal da cliente.",
        severity: "success",
      });
      setSnackbarOpen(true);
      history.push("/portal-cliente");
    }, 1000);
  };

  return (
    <div className="register-page">
      <div className="register-page__background" />
      <div className="register-page__glow register-page__glow--top" />
      <div className="register-page__glow register-page__glow--bottom" />

      <main className="register-page__shell">
        <section className="register-page__hero">
          <span className="register-page__eyebrow">Cadastro inicial</span>
          <Typography component="h1" className="register-page__title">
            Um onboarding pensado para clientes pessoa fisica e empresa.
          </Typography>
          <Typography component="p" className="register-page__text">
            Esta etapa organiza os dados de acesso e o cadastro inicial para
            preparar a futura area do cliente com mais seguranca, clareza e
            eficiencia operacional.
          </Typography>

          <div className="register-page__hero-card">
            <img
              src={heroImage}
              alt={APP_STRINGS.brand.fullName}
              className="register-page__hero-image"
            />
            <div className="register-page__hero-copy">
              <span className="register-page__hero-tag">Base do portal</span>
              <strong>Credenciais e dados organizados desde o primeiro acesso.</strong>
              <p>
                O cadastro ja prepara o projeto para login real, onboarding
                juridico, documentos, timeline do atendimento e area reservada
                para clientes.
              </p>
            </div>
          </div>
        </section>

        <section className="register-page__panel">
          <div className="register-page__panel-header">
            <span className="register-page__brand">
              {APP_STRINGS.brand.desktop}
            </span>
            <RouterLink className="register-page__back" to="/login">
              Ja tenho acesso
            </RouterLink>
          </div>

          <div className="register-page__card">
            <span className="register-page__card-tag">Cadastro</span>
            <Typography component="h2" className="register-page__card-title">
              Criar acesso inicial
            </Typography>
            <Typography component="p" className="register-page__card-text">
              Escolha o perfil de cadastro e preencha os dados principais de
              acesso e identificacao.
            </Typography>

            <div className="register-page__type-switch">
              <button
                type="button"
                className={`register-page__type-button${
                  form.profileType === "pf" ? " is-active" : ""
                }`}
                onClick={() => handleProfileType("pf")}
              >
                Pessoa fisica
              </button>
              <button
                type="button"
                className={`register-page__type-button${
                  form.profileType === "pj" ? " is-active" : ""
                }`}
                onClick={() => handleProfileType("pj")}
              >
                Pessoa juridica
              </button>
            </div>

            <form className="register-page__form" onSubmit={handleSubmit} noValidate>
              <div className="register-page__grid">
                {form.profileType === "pf" ? (
                  <div className="register-page__field">
                    <CustomInput
                      labelText="Nome completo"
                      id="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        autoComplete: "name",
                        "aria-invalid": !!errors.fullName,
                      }}
                    />
                    <div className={`register-page__helper${errors.fullName ? " register-page__helper--error" : ""}`}>
                      {errors.fullName || "Nome do cliente titular."}
                    </div>
                  </div>
                ) : (
                  <div className="register-page__field">
                    <CustomInput
                      labelText="Razao social"
                      id="companyName"
                      value={form.companyName}
                      onChange={handleChange}
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        autoComplete: "organization",
                        "aria-invalid": !!errors.companyName,
                      }}
                    />
                    <div className={`register-page__helper${errors.companyName ? " register-page__helper--error" : ""}`}>
                      {errors.companyName || "Nome empresarial principal."}
                    </div>
                  </div>
                )}

                <div className="register-page__field">
                  <CustomInput
                    labelText="E-mail de acesso"
                    id="email"
                    value={form.email}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "email",
                      autoComplete: "email",
                      "aria-invalid": !!errors.email,
                    }}
                  />
                  <div className={`register-page__helper${errors.email ? " register-page__helper--error" : ""}`}>
                    {errors.email || "Esse e-mail sera usado para login e comunicacoes."}
                  </div>
                </div>

                <div className="register-page__field">
                  <CustomInput
                    labelText="Senha"
                    id="password"
                    value={form.password}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "password",
                      autoComplete: "new-password",
                      "aria-invalid": !!errors.password,
                    }}
                  />
                  <div className={`register-page__helper${errors.password ? " register-page__helper--error" : ""}`}>
                    {errors.password || "Crie uma senha com no minimo 6 caracteres."}
                  </div>
                </div>

                <div className="register-page__field">
                  <CustomInput
                    labelText="Confirmar senha"
                    id="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "password",
                      autoComplete: "new-password",
                      "aria-invalid": !!errors.confirmPassword,
                    }}
                  />
                  <div className={`register-page__helper${errors.confirmPassword ? " register-page__helper--error" : ""}`}>
                    {errors.confirmPassword || "Repita a senha para confirmar."}
                  </div>
                </div>

                <div className="register-page__field">
                  <CustomInput
                    labelText="Telefone"
                    id="phone"
                    value={form.phone}
                    onChange={handleChange}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      type: "tel",
                      autoComplete: "tel",
                      "aria-invalid": !!errors.phone,
                    }}
                  />
                  <div className={`register-page__helper${errors.phone ? " register-page__helper--error" : ""}`}>
                    {errors.phone || "Preferencialmente com WhatsApp."}
                  </div>
                </div>

                {form.profileType === "pf" ? (
                  <>
                    <div className="register-page__field">
                      <CustomInput
                        labelText="CPF"
                        id="cpf"
                        value={form.cpf}
                        onChange={handleChange}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          inputMode: "numeric",
                          "aria-invalid": !!errors.cpf,
                        }}
                      />
                      <div className={`register-page__helper${errors.cpf ? " register-page__helper--error" : ""}`}>
                        {errors.cpf || "Documento principal para identificacao."}
                      </div>
                    </div>

                    <div className="register-page__field">
                      <CustomInput
                        labelText="Data de nascimento"
                        id="birthDate"
                        value={form.birthDate}
                        onChange={handleChange}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          type: "date",
                          "aria-invalid": !!errors.birthDate,
                        }}
                        labelProps={{ shrink: true }}
                      />
                      <div className={`register-page__helper${errors.birthDate ? " register-page__helper--error" : ""}`}>
                        {errors.birthDate || "Ajuda na validacao do cadastro."}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="register-page__field">
                      <CustomInput
                        labelText="Nome fantasia"
                        id="tradeName"
                        value={form.tradeName}
                        onChange={handleChange}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          autoComplete: "organization",
                        }}
                      />
                      <div className="register-page__helper">
                        Se houver um nome de apresentacao comercial.
                      </div>
                    </div>

                    <div className="register-page__field">
                      <CustomInput
                        labelText="CNPJ"
                        id="cnpj"
                        value={form.cnpj}
                        onChange={handleChange}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          inputMode: "numeric",
                          "aria-invalid": !!errors.cnpj,
                        }}
                      />
                      <div className={`register-page__helper${errors.cnpj ? " register-page__helper--error" : ""}`}>
                        {errors.cnpj || "Documento principal da empresa."}
                      </div>
                    </div>

                    <div className="register-page__field register-page__field--full">
                      <CustomInput
                        labelText="Responsavel pelo acesso"
                        id="contactName"
                        value={form.contactName}
                        onChange={handleChange}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          autoComplete: "name",
                          "aria-invalid": !!errors.contactName,
                        }}
                      />
                      <div className={`register-page__helper${errors.contactName ? " register-page__helper--error" : ""}`}>
                        {errors.contactName || "Pessoa que fara o primeiro acesso pela empresa."}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="register-page__consent">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.acceptTerms}
                      onChange={handleChange}
                      color="primary"
                      id="acceptTerms"
                      name="acceptTerms"
                    />
                  }
                  label="Concordo com o cadastro inicial e com o tratamento dos dados para acesso a area do cliente."
                />
                {errors.acceptTerms ? (
                  <div className="register-page__helper register-page__helper--error">
                    {errors.acceptTerms}
                  </div>
                ) : null}
              </div>

              <div className="register-page__actions">
                <Button
                  type="submit"
                  className="register-page__primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Criar cadastro"}
                </Button>

                <RouterLink className="register-page__secondary" to="/login">
                  Ja tenho conta
                </RouterLink>
              </div>
            </form>

            <div className="register-page__note">
              Frontend inicial pronto para futura integracao com backend,
              autenticacao, area do cliente e fluxo de aprovacao cadastral.
            </div>

            <RouterLink className="register-page__demo-link" to="/portal-cliente">
              Abrir portal demonstrativo com dados ja cadastrados
            </RouterLink>
          </div>
        </section>
      </main>

      <GlobalMessage
        onClick={() => setSnackbarOpen(false)}
        snackbarOpen={snackbarOpen}
        message={feedback}
      />
    </div>
  );
}
